import api from './api'

export interface LiveStream {
  id: number
  host: {
    id: number
    username: string
    firstName: string
    lastName: string
    profileImage?: string
  }
  title: string
  description?: string
  thumbnailUrl?: string
  streamUrl?: string
  videoUrl?: string
  status: 'PENDING' | 'LIVE' | 'ENDED' | 'SCHEDULED'
  viewerCount: number
  likeCount: number
  commentCount: number
  shareCount: number
  maxViewers: number
  durationSeconds: number
  maxDurationSeconds: number
  isPublic: boolean
  startedAt?: string
  endedAt?: string
  createdAt: string
}

export interface LiveStreamCreateRequest {
  title: string
  description?: string
  thumbnailUrl?: string
  scheduledAt?: string
}

export interface StreamStats {
  streamId: number
  title: string
  status: string
  viewerCount: number
  maxViewers: number
  likeCount: number
  commentCount: number
  shareCount: number
  durationSeconds: number
  startedAt: string
  endedAt?: string
}

const liveStreamService = {
  // Start a new live stream immediately
  startStream: async (title?: string, description?: string, isPublic: boolean = true) => {
    const response = await api.post('/live-streams/start', { title, description, isPublic })
    return response.data
  },

  // Schedule a future live stream
  scheduleStream: async (data: LiveStreamCreateRequest) => {
    const response = await api.post('/live-streams/schedule', data)
    return response.data
  },

  // End a live stream
  endStream: async (streamId: number) => {
    const response = await api.post(`/live-streams/${streamId}/end`)
    return response.data
  },

  // Get stream by ID
  getStream: async (streamId: number) => {
    const response = await api.get(`/live-streams/${streamId}`)
    return response.data
  },

  // Get all active live streams
  getActiveStreams: async (page: number = 0, size: number = 20) => {
    const response = await api.get('/live-streams/active', { params: { page, size } })
    return response.data
  },

  // Get scheduled streams
  getScheduledStreams: async () => {
    const response = await api.get('/live-streams/scheduled')
    return response.data
  },

  // Get streams by user
  getUserStreams: async (hostId: number) => {
    const response = await api.get(`/live-streams/user/${hostId}`)
    return response.data
  },

  // Get current user's active stream
  getMyActiveStream: async () => {
    const response = await api.get('/live-streams/my-stream')
    return response.data
  },

  // Join a live stream
  joinStream: async (streamId: number) => {
    const response = await api.post(`/live-streams/${streamId}/join`)
    return response.data
  },

  // Leave a live stream
  leaveStream: async (streamId: number) => {
    const response = await api.post(`/live-streams/${streamId}/leave`)
    return response.data
  },

  // Send a like to stream
  sendLike: async (streamId: number) => {
    const response = await api.post(`/live-streams/${streamId}/like`)
    return response.data
  },

  // Get stream statistics
  getStreamStats: async (streamId: number) => {
    const response = await api.get(`/live-streams/${streamId}/stats`)
    return response.data
  },

  // Get remaining time
  getTimeRemaining: async (streamId: number) => {
    const response = await api.get(`/live-streams/${streamId}/time-remaining`)
    return response.data
  },

  // Save stream video
  saveStreamVideo: async (streamId: number, videoUrl: string, videoSizeBytes?: number) => {
    const response = await api.post(`/live-streams/${streamId}/save-video`, { videoUrl, videoSizeBytes })
    return response.data
  },

  // Delete stream
  deleteStream: async (streamId: number) => {
    const response = await api.delete(`/live-streams/${streamId}`)
    return response.data
  },

  // Health check
  healthCheck: async () => {
    const response = await api.get('/live-streams/health')
    return response.data
  }
}

export default liveStreamService